import { generateCodeVerifier, generateCodeChallenge, generateState } from './pkce';

const X_AUTH_URL = 'https://x.com/i/oauth2/authorize';
const X_CLIENT_ID = import.meta.env.VITE_X_CLIENT_ID || '';

const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

// X OAuth scopes
const SCOPES = 'tweet.read users.read offline.access users.email';

// Google OAuth scopes
const GOOGLE_SCOPES = 'openid email profile';

/**
 * Local storage keys for OAuth state
 * Using localStorage instead of sessionStorage to persist through
 * cross-origin redirects and tab reloads on mobile browsers
 */
const STORAGE_KEYS = {
  CODE_VERIFIER: 'oauth_code_verifier',
  STATE: 'oauth_state',
  REDIRECT_URI: 'oauth_redirect_uri',
  ORIGIN_PATH: 'oauth_origin_path',
  TIMESTAMP: 'oauth_timestamp',
  LINK_MODE: 'oauth_link_mode', // 'login' or 'link'
  LINK_PROVIDER: 'oauth_link_provider', // 'x' or 'google'
  CODE: 'oauth_code', // Authorization code for create separate account flow
};

/**
 * OAuth state expiration time (15 minutes)
 */
const OAUTH_STATE_EXPIRATION_MS = 15 * 60 * 1000;

/**
 * Initiate OAuth flow by redirecting to X authorization URL
 */
export async function initiateOAuthFlow(redirectUri: string, originPath?: string): Promise<void> {
  // Generate PKCE values
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();

  // Store values in local storage for callback validation
  localStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, codeVerifier);
  localStorage.setItem(STORAGE_KEYS.STATE, state);
  localStorage.setItem(STORAGE_KEYS.REDIRECT_URI, redirectUri);
  localStorage.setItem(STORAGE_KEYS.TIMESTAMP, Date.now().toString());

  if (originPath) {
    localStorage.setItem(STORAGE_KEYS.ORIGIN_PATH, originPath);
  }

  // Build authorization URL
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: X_CLIENT_ID,
    redirect_uri: redirectUri,
    scope: SCOPES,
    state: state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  const authUrl = `${X_AUTH_URL}?${params.toString()}`;

  // Redirect to X authorization page
  window.location.href = authUrl;
}

/**
 * Retrieve OAuth state from local storage
 * Automatically clears and returns empty state if expired (>15 minutes old)
 */
export function getOAuthState() {
  const timestamp = localStorage.getItem(STORAGE_KEYS.TIMESTAMP);

  // Check if state has expired
  if (timestamp) {
    const age = Date.now() - parseInt(timestamp, 10);
    if (age > OAUTH_STATE_EXPIRATION_MS) {
      // State has expired, clear it
      clearOAuthState();
      return {
        codeVerifier: null,
        state: null,
        redirectUri: null,
        originPath: null,
        linkMode: null,
        linkProvider: null,
      };
    }
  }

  // Return stored state if valid
  return {
    codeVerifier: localStorage.getItem(STORAGE_KEYS.CODE_VERIFIER),
    state: localStorage.getItem(STORAGE_KEYS.STATE),
    redirectUri: localStorage.getItem(STORAGE_KEYS.REDIRECT_URI),
    originPath: localStorage.getItem(STORAGE_KEYS.ORIGIN_PATH),
    linkMode: localStorage.getItem(STORAGE_KEYS.LINK_MODE) as 'login' | 'link' | null,
    linkProvider: localStorage.getItem(STORAGE_KEYS.LINK_PROVIDER) as 'x' | 'google' | null,
    code: localStorage.getItem(STORAGE_KEYS.CODE),
  };
}

/**
 * Clear OAuth state from local storage
 */
export function clearOAuthState() {
  localStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
  localStorage.removeItem(STORAGE_KEYS.STATE);
  localStorage.removeItem(STORAGE_KEYS.REDIRECT_URI);
  localStorage.removeItem(STORAGE_KEYS.ORIGIN_PATH);
  localStorage.removeItem(STORAGE_KEYS.TIMESTAMP);
  localStorage.removeItem(STORAGE_KEYS.LINK_MODE);
  localStorage.removeItem(STORAGE_KEYS.LINK_PROVIDER);
  localStorage.removeItem(STORAGE_KEYS.CODE);
}

/**
 * Store authorization code in OAuth state
 * Used when showing AccountExistsModal to preserve code for "create separate account" flow
 */
export function storeAuthorizationCode(code: string) {
  localStorage.setItem(STORAGE_KEYS.CODE, code);
}

/**
 * Force create mode - indicates user wants to create separate account
 * despite email already existing with another provider
 */
const FORCE_CREATE_KEY = 'oauth_force_create';

export function setForceCreateMode(provider: 'x' | 'google') {
  localStorage.setItem(FORCE_CREATE_KEY, provider);
}

export function getForceCreateMode(): 'x' | 'google' | null {
  return localStorage.getItem(FORCE_CREATE_KEY) as 'x' | 'google' | null;
}

export function clearForceCreateMode() {
  localStorage.removeItem(FORCE_CREATE_KEY);
}

/**
 * Initiate Google OAuth flow by redirecting to Google authorization URL
 */
export async function initiateGoogleOAuthFlow(redirectUri: string, originPath?: string): Promise<void> {
  // Generate state for CSRF protection
  const state = generateState();

  // Store values in local storage for callback validation
  localStorage.setItem(STORAGE_KEYS.STATE, state);
  localStorage.setItem(STORAGE_KEYS.REDIRECT_URI, redirectUri);
  localStorage.setItem(STORAGE_KEYS.TIMESTAMP, Date.now().toString());

  if (originPath) {
    localStorage.setItem(STORAGE_KEYS.ORIGIN_PATH, originPath);
  }

  // Build authorization URL
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: GOOGLE_SCOPES,
    state: state,
    access_type: 'offline',
    prompt: 'consent',
  });

  const authUrl = `${GOOGLE_AUTH_URL}?${params.toString()}`;

  // Redirect to Google authorization page
  window.location.href = authUrl;
}
